import { buildGeneralTab, showWidgetEditDialog } from '@juvare/common-form-ui/WidgetEditDialog';
import { FormHelperErrorDescription } from '@juvare/common-form-ui/WidgetForm/Components';
import { defaultWidgetLabelProvider, getVisibleError, handleWidgetUpdate, renderForm } from '@juvare/common-form-ui/WidgetForm/Utils';
import { WidgetHandler } from '@juvare/common-form-ui/WidgetHandler';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import SendTimeExtensionOutlinedIcon from '@mui/icons-material/SendTimeExtensionOutlined';
import { Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Radio, RadioGroup, Select, Typography, } from '@mui/material';
import React from 'react';
import { useAlertUiTranslation } from '../../../../hooks';
import { type } from './types';
const WidgetDeliveryOrder = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const { t } = useAlertUiTranslation();
    const error = getVisibleError(props);
    const value = props.getData();
    const [selectedMode, setSelectedMode] = React.useState((value === null || value === void 0 ? void 0 : value.deliveryMode) || 'blast');
    const [textSelectedValue, setTextSelectedValue] = React.useState((value === null || value === void 0 ? void 0 : value.textDelay) !== undefined
        ? value === null || value === void 0 ? void 0 : value.textDelay
        : (_a = props.config.escalationTextValues.find((v) => v.value === props.config.defaultEscalationTextValue)) === null || _a === void 0 ? void 0 : _a.value);
    const [voiceSelectedValue, setVoiceSmsSelectedValue] = React.useState((value === null || value === void 0 ? void 0 : value.voiceDelay) !== undefined
        ? value === null || value === void 0 ? void 0 : value.voiceDelay
        : (_b = props.config.escalationVoiceValues.find((v) => v.value === props.config.defaultEscalationVoiceValue)) === null || _b === void 0 ? void 0 : _b.value);
    const smsSelected = (_d = (_c = props.config) === null || _c === void 0 ? void 0 : _c.selectedChannels) === null || _d === void 0 ? void 0 : _d.includes('sms');
    const voiceSelected = (_f = (_e = props.config) === null || _e === void 0 ? void 0 : _e.selectedChannels) === null || _f === void 0 ? void 0 : _f.includes('voice');
    const smsVisible = smsSelected && (((_g = props.config) === null || _g === void 0 ? void 0 : _g.selectedChannels) || []).filter((ch) => ch !== 'sms' && ch !== 'voice').length > 0;
    const escalationEnabled = (props.config.selectedChannels || []).length > 1 && (smsSelected || voiceSelected);
    const handleSelectedModeChange = (event) => {
        setSelectedMode(event.target.value);
    };
    const handleSmsEscalationChange = (event) => {
        setTextSelectedValue(Number(event.target.value));
    };
    const handleVoiceEscalationChange = (event) => {
        setVoiceSmsSelectedValue(Number(event.target.value));
    };
    const setValue = React.useCallback((newValue) => {
        props.setData(newValue);
        if (JSON.stringify(value) !== JSON.stringify(newValue)) {
            renderForm(props, { changed: true });
        }
    }, [props, value]);
    React.useEffect(() => {
        if (selectedMode === 'escalation' && !escalationEnabled) {
            setSelectedMode('blast');
        }
    }, [escalationEnabled, selectedMode]);
    React.useEffect(() => {
        if (selectedMode === 'blast') {
            setValue({ deliveryMode: selectedMode });
        }
        else {
            const textDelay = smsVisible ? textSelectedValue : undefined;
            const voiceDelay = voiceSelected ? voiceSelectedValue : undefined;
            setValue({ deliveryMode: selectedMode, textDelay, voiceDelay });
        }
    }, [selectedMode, setValue, smsVisible, textSelectedValue, voiceSelected, voiceSelectedValue, props.config.selectedChannels]);
    function getFirstEscalationLabel() {
        if (props.config.selectedChannels) {
            return t('pages.edit.timeBetweenEscalations', {
                channels1: props.config.selectedChannels.filter((ch) => ch !== 'sms' && ch !== 'voice').join(', '),
                channels2: t(`common.channels.sms`).toLowerCase(),
            });
        }
        return '';
    }
    function getSecondEscalationLabel() {
        if (props.config.selectedChannels) {
            const updatedChannels = props.config.selectedChannels.map((channel) => (channel === 'sms' ? 'text' : channel));
            return t('pages.edit.timeBetweenEscalations', {
                channels1: updatedChannels.filter((ch) => ch !== 'voice').join(', '),
                channels2: t(`common.channels.voice`).toLowerCase(),
            });
        }
        return '';
    }
    return (React.createElement(Grid, { container: true, spacing: 2 },
        React.createElement(Grid, { size: 12 },
            React.createElement(FormControl, { component: 'fieldset', fullWidth: true },
                React.createElement(RadioGroup, { row: true, value: selectedMode, onChange: handleSelectedModeChange },
                    React.createElement(Grid, { size: 6 },
                        React.createElement(FormControlLabel, { value: 'blast', disabled: (_h = props.config) === null || _h === void 0 ? void 0 : _h.disabled, control: React.createElement(Radio, null), label: t(`pages.edit.blast`) })),
                    React.createElement(Grid, { size: 6 },
                        React.createElement(FormControlLabel, { value: 'escalation', disabled: !escalationEnabled || ((_j = props.config) === null || _j === void 0 ? void 0 : _j.disabled), control: React.createElement(Radio, null), label: t(`pages.edit.escalation`) }))))),
        selectedMode === 'escalation' && (React.createElement(React.Fragment, null,
            React.createElement(Grid, { size: 12 },
                React.createElement(Divider, { variant: 'fullWidth' })),
            React.createElement(Grid, { size: 12 },
                React.createElement(Typography, { sx: { color: (theme) => theme.palette.grey['700'], fontSize: '14px' } }, t(`pages.edit.escalationOrder`))),
            React.createElement(Grid, { size: 12 },
                React.createElement(FormControl, { component: 'fieldset', fullWidth: true },
                    React.createElement(Grid, { container: true, spacing: 2 },
                        smsVisible && (React.createElement(Grid, { size: {
                                xs: 12,
                                sm: smsVisible && voiceSelected ? 6 : 12,
                            } },
                            React.createElement(FormControl, { fullWidth: true, error: !!error },
                                React.createElement(InputLabel, null, getFirstEscalationLabel()),
                                React.createElement(Select, { label: getFirstEscalationLabel(), value: textSelectedValue, disabled: (_k = props.config) === null || _k === void 0 ? void 0 : _k.disabled, sx: { backgroundColor: 'background.paper', width: '100%' }, onChange: handleSmsEscalationChange, inputProps: { 'aria-label': getFirstEscalationLabel() } }, props.config.escalationTextValues.map((value) => (React.createElement(MenuItem, { key: value.value, value: value.value }, value.label)))),
                                React.createElement(FormHelperErrorDescription, { error: error })))),
                        voiceSelected && (React.createElement(Grid, { size: {
                                xs: 12,
                                sm: smsVisible && voiceSelected ? 6 : 12,
                            } },
                            React.createElement(FormControl, { fullWidth: true, error: !!error },
                                React.createElement(InputLabel, null, getSecondEscalationLabel()),
                                React.createElement(Select, { label: getSecondEscalationLabel(), value: voiceSelectedValue, disabled: (_l = props.config) === null || _l === void 0 ? void 0 : _l.disabled, sx: { backgroundColor: 'background.paper', width: '100%' }, onChange: handleVoiceEscalationChange, inputProps: { 'aria-label': getFirstEscalationLabel() } }, props.config.escalationVoiceValues.map((value) => (React.createElement(MenuItem, { key: value.value, value: value.value }, value.label)))),
                                React.createElement(FormHelperErrorDescription, { error: error })))))))))));
};
const WidgetDeliveryOrderEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, {
            title: 'Delivery Order',
        }, buildGeneralTab(props, {
            key: true,
            children: [
                {
                    type: 'form.dataGrid',
                    key: 'escalationTextValues',
                    label: 'Text Escalation Values',
                    children: [
                        {
                            type: 'form.text',
                            key: 'value',
                            label: 'Value',
                        },
                        {
                            type: 'form.text',
                            key: 'label',
                            label: 'Label',
                        },
                    ],
                },
                {
                    type: 'form.dataGrid',
                    key: 'escalationVoiceValues',
                    label: 'Voice Escalation Values',
                    children: [
                        {
                            type: 'form.text',
                            key: 'value',
                            label: 'Value',
                        },
                        {
                            type: 'form.text',
                            key: 'label',
                            label: 'Label',
                        },
                    ],
                },
                {
                    type: 'form.select',
                    key: 'defaultEscalationTextValue',
                    label: 'Default Text Escalation Value',
                    values: props.config.escalationTextValues,
                    defaultValue: props.config.defaultEscalationTextValue,
                    validate: {
                        required: true,
                    },
                },
                {
                    type: 'form.select',
                    key: 'defaultEscalationVoiceValue',
                    label: 'Default Voice Escalation Value',
                    values: props.config.escalationVoiceValues,
                    defaultValue: props.config.defaultEscalationVoiceValue,
                    validate: {
                        required: true,
                    },
                },
            ],
        })) }),
        React.createElement(WidgetDeliveryOrder, Object.assign({}, props))));
};
export const widgetDeliveryOrder = {
    type,
    createWidget: () => ({
        type,
        key: 'deliveryOrder',
        defaultEscalationTextValue: 5,
        defaultEscalationVoiceValue: 30,
        escalationTextValues: [
            { label: '0 minutes', value: 0 },
            { label: '5 minutes', value: 5 },
            { label: '10 minutes', value: 10 },
            { label: '15 minutes', value: 15 },
            { label: '30 minutes', value: 30 },
            { label: '60 minutes', value: 60 },
        ],
        escalationVoiceValues: [
            { label: '0 minutes', value: 0 },
            { label: '5 minutes', value: 5 },
            { label: '10 minutes', value: 10 },
            { label: '15 minutes', value: 15 },
            { label: '30 minutes', value: 30 },
            { label: '60 minutes', value: 60 },
        ],
    }),
    init: (props) => {
        const data = props.getData();
        if (data === null) {
            props.setData({ deliveryMode: 'blast' });
        }
    },
    update: (props) => {
        var _a;
        const handler = new WidgetHandler(props);
        const prevData = handler.config.selectedChannels;
        handler.config.selectedChannels = (_a = handler.formData) === null || _a === void 0 ? void 0 : _a.channels;
        return prevData !== handler.config.selectedChannels || handleWidgetUpdate(props);
    },
    component: WidgetDeliveryOrder,
    icon: React.createElement(SendTimeExtensionOutlinedIcon, null),
    editorComponent: WidgetDeliveryOrderEditor,
};
